/**
 * Metadados de navegação por rota do shell autenticado.
 *
 * `parent === null` marca uma RAIZ: o cabeçalho mostra o layout completo
 * (sino + Elos + hambúrguer) e NÃO exibe a seta de voltar. Nas demais telas,
 * o cabeçalho troca o sino pela seta ‹ (voltar um nível) e usa `title` como
 * rótulo — subindo sempre para `parent`, previsível, sem depender do
 * histórico do navegador (funciona igual em deep-link).
 *
 * Sprint 3 (barra fixa inferior — Início/Calendário/Módulos/Núcleo): as 4
 * abas da barra são RAIZES (parent: null), já que são destinos de primeiro
 * nível alcançáveis a qualquer momento por um toque na barra — não faz
 * sentido uma delas mostrar "‹ voltar para outra aba". Toda tela que "mora"
 * dentro de uma aba aponta o parent para ELA (não mais para /inicio): as
 * telas de Biblioteca/Eventos/Reservas apontam para /modulos; as de
 * Usuários/Visitantes/Ministérios/Módulos(config)/Igreja apontam para /nucleo.
 *
 * Rotas com parâmetro (ex.: /admin/ministerios/:id) não cabem no mapa exato —
 * entram em META_DINAMICO, resolvidas por padrão de segmentos.
 */
export interface NavMeta {
  title: string;
  /** Rota do nível acima; `null` numa raiz (aba da barra fixa, sem voltar). */
  parent: string | null;
}

const META: Record<string, NavMeta> = {
  "/inicio": { title: "Início", parent: null },
  "/calendario": { title: "Calendário", parent: null },
  "/modulos": { title: "Central Elos", parent: null },
  "/nucleo": { title: "Administração", parent: null },

  // Módulo Biblioteca: hub + subtelas do gestor — alcançado pela aba Módulos.
  "/admin": { title: "Biblioteca", parent: "/modulos" },
  "/admin/emprestimos": { title: "Empréstimos", parent: "/admin" },
  "/admin/livros": { title: "Acervo de livros", parent: "/admin" },
  "/admin/categorias": { title: "Categorias", parent: "/admin" },
  "/admin/visualizar-como-usuario": { title: "Visão do membro", parent: "/admin" },

  // Núcleo (Perfil de Plataforma) — alcançado pela aba Núcleo.
  "/admin/usuarios": { title: "Usuários", parent: "/nucleo" },
  "/admin/ministerios": { title: "Ministérios", parent: "/nucleo" },
  "/admin/modulos": { title: "Elos da igreja", parent: "/nucleo" },
  "/admin/igreja": { title: "Igreja", parent: "/nucleo" },

  // Sprint 3 — Eventos e Reservas, alcançados pela aba Módulos.
  "/eventos": { title: "Eventos", parent: "/modulos" },
  "/reservas": { title: "Reservas", parent: "/modulos" },
  "/reservas/ambientes": { title: "Ambientes", parent: "/reservas" },

  // Fase 3 (frontend definitivo) — Escala, alcançado pela aba Módulos.
  "/escalas": { title: "Escala", parent: "/modulos" },
  "/escalas/nova": { title: "Nova escala", parent: "/escalas" },

  // Comunicados — alcançado pela aba Módulos (sem ícone na barra inferior).
  "/comunicados": { title: "Comunicados", parent: "/modulos" },
  "/comunicados/novo": { title: "Novo comunicado", parent: "/comunicados" },

  // Visitantes — alcançado pela aba Módulos (sem ícone na barra inferior).
  "/visitantes": { title: "Visitantes", parent: "/modulos" },
  "/visitantes/novo": { title: "Novo visitante", parent: "/visitantes" },
  "/visitantes/informar": { title: "Novo visitante", parent: "/visitantes" },

  // Meu perfil — tela própria da pessoa, aberta pelo menu "Conta" (hambúrguer).
  "/perfil": { title: "Meu perfil", parent: "/inicio" },
  // Telas da seção "Conta" do menu (hambúrguer): sem estas entradas o cabeçalho ficava sem a seta ‹.
  "/conta/seguranca": { title: "Segurança da conta", parent: "/inicio" },
  "/conta/notificacoes": { title: "Notificações", parent: "/inicio" },

  // Ajuda do Elos (tutorial do membro) — aberta pelo item "Ajuda do Elos" do menu Conta.
  // As lições (/ajuda/:licaoId) ficam em META_DINAMICO e voltam para a lista.
  "/ajuda": { title: "Ajuda do Elos", parent: "/inicio" },
  "/ajuda/apresentacao": { title: "Ajuda do Elos", parent: "/inicio" },
  "/ajuda/concluida": { title: "Ajuda do Elos", parent: "/ajuda" },

  // Área do membro: a Biblioteca na visão de quem não tem acesso administrativo.
  "/usuario": { title: "Biblioteca", parent: "/modulos" },
};

/** Rotas com parâmetro. A primeira que casar vence (ordem importa). */
const META_DINAMICO: { pattern: RegExp; meta: NavMeta }[] = [
  {
    // Uma lição da Ajuda do Elos — volta para a lista de lições.
    pattern: /^\/ajuda\/[^/]+$/,
    meta: { title: "Ajuda do Elos", parent: "/ajuda" },
  },
  {
    // Detalhe de ministério (admin) — volta para a lista.
    pattern: /^\/admin\/ministerios\/[^/]+$/,
    meta: { title: "Ministério", parent: "/admin/ministerios" },
  },
  {
    // Ministério visto pelo LÍDER (DA-004, fora do guard de admin) — chegou
    // pelo card de governança que continua no Início.
    pattern: /^\/ministerios\/[^/]+$/,
    meta: { title: "Meu ministério", parent: "/inicio" },
  },
  {
    // Detalhe de uma escala — volta para a lista.
    pattern: /^\/escalas\/[^/]+$/,
    meta: { title: "Escala", parent: "/escalas" },
  },
  {
    // Edição de um comunicado — volta para a lista.
    pattern: /^\/comunicados\/[^/]+\/editar$/,
    meta: { title: "Editar comunicado", parent: "/comunicados" },
  },
  {
    // Avaliar/editar um Visitante pendente (cadastrado por líder) — volta para a lista.
    pattern: /^\/visitantes\/informados\/[^/]+\/(avaliar|editar)$/,
    meta: { title: "Visitante pendente", parent: "/visitantes" },
  },
  {
    // Edição do cadastro de um visitante — volta para a lista.
    pattern: /^\/visitantes\/[^/]+\/editar$/,
    meta: { title: "Editar visitante", parent: "/visitantes" },
  },
  {
    // Ficha do visitante — volta para a lista.
    pattern: /^\/visitantes\/[^/]+$/,
    meta: { title: "Visitante", parent: "/visitantes" },
  },
];

/** Resolve os metadados da rota. Rota desconhecida cai como raiz (sem voltar). */
export function getNavMeta(pathname: string): NavMeta {
  const exata = META[pathname];
  if (exata) return exata;
  const dinamica = META_DINAMICO.find((d) => d.pattern.test(pathname));
  return dinamica?.meta ?? { title: "Elos", parent: null };
}
